import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Title,
  Code,
  Divider,
  Switch,
  Badge,
  PasswordInput,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconBug,
  IconCode,
  IconTerminal2,
  IconCheck,
  IconX,
  IconKey,
  IconCopy,
  IconTrash,
} from '@tabler/icons-react';
import { toggleDebugMode, isDebugModeActive } from '@services/api/axios-instance';

interface SecretModalProps {
  opened: boolean;
  onClose: () => void;
}

export const SecretModal: React.FC<SecretModalProps> = ({ opened, onClose }) => {
  const [isDebugEnabled, setIsDebugEnabled] = useState(isDebugModeActive());
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [tokenSaved, setTokenSaved] = useState(false);

  // Cargar token al abrir el modal
  useEffect(() => {
    if (opened) {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        setToken(savedToken);
        setTokenSaved(true);
        // Habilitar modo debug si hay token
        if (!isDebugEnabled) {
          handleToggleDebug();
        }
      }
    }
  }, [opened]);

  const handleToggleDebug = () => {
    const newState = toggleDebugMode();
    setIsDebugEnabled(newState);
  };

  const handleSaveToken = () => {
    if (token.trim()) {
      localStorage.setItem('token', token);
      setTokenSaved(true);

      // Habilitar modo debug si hay token
      if (!isDebugEnabled) {
        handleToggleDebug();
      }
    }
  };

  const handleClearToken = () => {
    localStorage.removeItem('token');
    setToken('');
    setTokenSaved(false);
  };

  const handleCopyToken = () => {
    window.navigator.clipboard.writeText(token);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group>
          <IconBug size={24} />
          <Title order={3}>Modo Desarrollador</Title>
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        <Text>
          ¡Has descubierto el modo desarrollador! Este es un espacio secreto para acceder a
          herramientas avanzadas.
        </Text>

        <Divider />

        <Group>
          <IconTerminal2 size={20} />
          <Text fw={500}>API Endpoints</Text>
        </Group>
        <Code block>
          {`GET /api/content
POST /api/content
PUT /api/content/:_id
DELETE /api/content/:_id`}
        </Code>

        <Divider />

        <Group>
          <IconCode size={20} />
          <Text fw={500}>Variables de Entorno</Text>
        </Group>
        <Code block>
          {`VITE_API_BASE_URL=https://api.example.com
VITE_APP_VERSION=1.0.0
VITE_DEBUG_MODE=true`}
        </Code>

        <Divider />

        <Group justify="space-between">
          <Group>
            <IconBug size={20} />
            <Text fw={500}>Modo Debug</Text>
          </Group>
          <Switch checked={isDebugEnabled} onChange={handleToggleDebug} size="md" color="blue" />
        </Group>

        {isDebugEnabled ? (
          <Badge color="green" variant="filled" leftSection={<IconCheck size={14} />}>
            Modo Debug Activado
          </Badge>
        ) : (
          <Badge color="gray" variant="filled" leftSection={<IconX size={14} />}>
            Modo Debug Desactivado
          </Badge>
        )}

        <Divider />

        <Group>
          <IconKey size={20} />
          <Text fw={500}>JWT Token</Text>
        </Group>

        <Group align="flex-end">
          <PasswordInput
            placeholder="Introduce tu JWT token"
            value={token}
            onChange={e => setToken(e.target.value)}
            style={{ flex: 1 }}
            label="Token de autenticación"
            description="Este token se usará para todas las peticiones HTTP"
            visibilityToggleIcon={() => (
              <span style={{ cursor: 'pointer' }} onClick={() => setShowToken(!showToken)}>
                {showToken ? <IconX size={16} /> : <IconCheck size={16} />}
              </span>
            )}
            visible={showToken}
          />
          <Tooltip label="Copiar token">
            <ActionIcon color="blue" variant="light" onClick={handleCopyToken} disabled={!token}>
              <IconCopy size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Limpiar token">
            <ActionIcon color="red" variant="light" onClick={handleClearToken} disabled={!token}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Button onClick={handleSaveToken} color="blue" disabled={!token || tokenSaved}>
          {tokenSaved ? 'Token guardado' : 'Guardar token'}
        </Button>

        {tokenSaved && (
          <Badge color="green" variant="filled" leftSection={<IconCheck size={14} />}>
            Token guardado correctamente
          </Badge>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={handleClose}>
            Cerrar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
